import { animated, easings, useSpring } from '@react-spring/web';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useMeasure } from 'react-use';
import styled from 'styled-components';

import type { unitMapping } from '#src/lib/style.ts';

const Wrapper = styled(animated.div)`
  position: relative;
  overflow: hidden;
`;
const Gap = styled.div<{ $gap: keyof typeof unitMapping }>`
  display: flex;
  height: calc(var(--unit) * ${({ $gap }) => $gap});
`;

const AnimatedContent = styled(animated.div)`
  position: relative;
`;

const CollapsedContentWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
`;

const AnimatedCollapsedContent = styled(animated.div)`
  position: relative;
`;

type Props = {
  open?: boolean;
  children: ReactNode;
  className?: string;
  gap?: keyof typeof unitMapping;
  collapsedContent?: ReactNode;
};
export const Collapse = ({
  open,
  children,
  className,
  gap = 2,
  collapsedContent,
}: Props) => {
  const [fullContentRef, { height: fullHeight }] = useMeasure<HTMLDivElement>();
  const [collapsedContentRef, { height: collapsedHeight }] =
    useMeasure<HTMLDivElement>();
  const [mounted, setMounted] = useState(false);

  const getTargetHeight = () => {
    if (open) {
      return fullHeight;
    }
    if (collapsedContent != null) {
      return collapsedHeight;
    }
    return 0;
  };

  const containerStyle = useSpring({
    to: {
      height: getTargetHeight(),
    },
    config: {
      easing: open ? easings.easeOutSine : easings.easeInSine,
    },
    immediate: !mounted,
  });

  const childrenStyle = useSpring({
    to: {
      opacity: open ? 1 : 0,
    },
    config: {
      easing: open ? easings.easeOutSine : easings.easeInSine,
    },
    immediate: !mounted,
  });

  const collapsedContentStyle = useSpring({
    to: {
      opacity: open ? 0 : 1,
    },
    config: {
      easing: open ? easings.easeOutSine : easings.easeInSine,
    },
    immediate: !mounted,
  });

  useEffect(() => {
    const timeout = setTimeout(() => {
      setMounted(true);
    }, 300);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <Wrapper className={className} style={containerStyle}>
      <div ref={fullContentRef}>
        {!!gap && <Gap $gap={gap} />}
        <AnimatedContent style={childrenStyle}>{children}</AnimatedContent>
      </div>
      {collapsedContent != null && (
        <CollapsedContentWrapper ref={collapsedContentRef}>
          <AnimatedCollapsedContent style={collapsedContentStyle}>
            {collapsedContent}
          </AnimatedCollapsedContent>
        </CollapsedContentWrapper>
      )}
    </Wrapper>
  );
};
